/**
 * Demo-Modus für den Klickdummy.
 *
 * Gebaut mit `npm run build:demo` entsteht eine einzige HTML-Datei, die ohne
 * Server, ohne Datenbank und ohne Internetverbindung läuft — zum Weitergeben,
 * Vorführen und Herumklicken.
 *
 * Die Daten sind keine Attrappe: `daten.json` ist die Antwort, die die echte
 * REST-Schnittstelle für die Demo-Prüfung liefert, einmal abgezogen und
 * eingebacken. Damit zeigt der Dummy dieselbe Gliederung, dieselben Summen und
 * dieselben Abgleichergebnisse wie die laufende Anwendung.
 *
 * Änderungen — Prüfstatus, Rückfragen, Abschluss — wirken im Arbeitsspeicher
 * und sind nach dem Neuladen wieder weg. Das ist für einen Klickdummy richtig:
 * Wer ihn herzeigt, will bei jedem Termin denselben Ausgangszustand.
 */

import rohdaten from './daten.json';
import type {
  Banktransaktion,
  Beleg,
  Dokument,
  Gruppe,
  Journal,
  Journalzeile,
  Journalzuordnung,
  Kennzahlen,
  Kommentar,
  Pruefung,
  PruefStatus,
  Saldenzeile,
  Schritt,
  SchrittStatus,
  Zahlungsbefund,
  Zuordnung,
} from '../lib/typen';

/** true, wenn diese Fassung als Klickdummy gebaut wurde. */
export const DEMO = import.meta.env.VITE_DEMO === '1';

interface Bestand {
  pruefung: Pruefung;
  belege: Beleg[];
  gliederung: Gruppe[];
  kennzahlen: Kennzahlen;
  transaktionen: Banktransaktion[];
  zuordnungen: Zuordnung[];
  /** Prüfstatus je Kontoauszugsposition. */
  transaktionStatus: Record<string, PruefStatus>;
  kommentare: Kommentar[];
  /** Das Journal wird im Dummy erst auf Knopfdruck „eingespielt". */
  journal?: Journal;
}

/** Tiefe Kopie, damit ein Zurücksetzen den Ausgangszustand wiederherstellt. */
function frisch(): Bestand {
  const kopie = JSON.parse(JSON.stringify(rohdaten)) as Bestand;
  // Die Daten stammen aus dem Server, der den Prüfstatus getrennt führt.
  // Im Klickdummy beginnt er leer — alles ist offen, so wie bei einer
  // frisch angelegten Prüfung.
  kopie.transaktionStatus ??= {};
  return kopie;
}

let bestand = frisch();

/** Die Prüfungs-ID des Klickdummys. Im Demo-Modus gibt es genau eine. */
export const DEMO_PRUEFUNG_ID = bestand.pruefung.id;

/** Rolle, zwischen der im Klickdummy umgeschaltet werden kann. */
let rolle: 'beirat' | 'verwaltung' = 'beirat';

export function demoRolle(): 'beirat' | 'verwaltung' {
  return rolle;
}

export function demoRolleSetzen(neu: 'beirat' | 'verwaltung') {
  rolle = neu;
}

export function demoZuruecksetzen() {
  bestand = frisch();
  rolle = 'beirat';
  demoSchritte = schritteFrisch();
  demoSalden = [];
}

/** Kurze Verzögerung, damit Ladezustände im Dummy sichtbar bleiben. */
/** Auf Cent runden — dieselbe Regel wie im Server. */
function runde2(n: number): number {
  return Math.round(n * 100) / 100;
}

function gleich<T>(wert: T, ms = 0): Promise<T> {
  return ms ? new Promise((f) => setTimeout(() => f(wert), ms)) : Promise.resolve(wert);
}

function kennzahlenNeu(): Kennzahlen {
  const b = bestand.belege;
  const zugeordnet = new Set(
    bestand.zuordnungen.filter((z) => z.status === 'zugeordnet').map((z) => z.transaktionId),
  ).size;
  return {
    belegeGesamt: b.length,
    belegeGeprueft: b.filter((x) => x.pruefstatus === 'geprueft').length,
    belegeRueckfrage: b.filter((x) => x.pruefstatus === 'rueckfrage').length,
    belegeOffen: b.filter((x) => x.pruefstatus === 'offen').length,
    pruefsumme: Math.round(b.reduce((s, x) => s + (x.brutto ?? 0), 0) * 100) / 100,
    rueckfragenOffen: bestand.kommentare.filter((k) => !k.eltern_id && k.status === 'offen').length,
    transaktionenGesamt: bestand.transaktionen.length,
    transaktionenZugeordnet: zugeordnet,
    abgleichquote: bestand.transaktionen.length
      ? Number((zugeordnet / bestand.transaktionen.length).toFixed(3))
      : 0,
    nichtZugeordneterBetrag: Math.round(
      bestand.transaktionen
        .filter(
          (t) =>
            t.richtung === 'ausgang' &&
            !bestand.zuordnungen.some(
              (z) => z.transaktionId === t.id && z.status === 'zugeordnet',
            ),
        )
        .reduce((s, t) => s + t.betrag, 0) * 100,
    ) / 100,
    zahldatumAbweichungen: [...befundeNeu().values()].filter(
      (z) => z.status === 'datum_weicht_ab',
    ).length,
    belegeOhneZahlung: [...befundeNeu().values()].filter((z) => z.status === 'keine_zahlung')
      .length,
    ...journalKennzahlen(),
    transaktionenGeprueft: Object.values(bestand.transaktionStatus).filter(
      (s) => s === 'geprueft',
    ).length,
    schritteGesamt: demoSchritte.length,
    schritteErledigt: demoSchritte.filter((x) => x.status === 'erledigt').length,
    fortschritt: demoSchritte.filter((x) => x.status === 'erledigt').length / demoSchritte.length,
    dokumenteGesamt: 0,
    dokumenteGeprueft: 0,
  };
}

// ─────────────────────────────────────────────────────────────
// Die fünf Prüfschritte im Klickdummy — Texte wie im Server, Stand im Speicher.
// ─────────────────────────────────────────────────────────────

const SCHRITT_VORLAGEN: Omit<Schritt, 'status' | 'anmerkung' | 'begonnenAm' | 'erledigtVon' | 'erledigtAm'>[] = [
  {
    nummer: 1,
    ueblich: 'Kontoauszüge, Gesamtabrechnung',
    titel: 'Kontenabgleich',
    kurz: 'Anfangs- und Endbestände',
    beschreibung:
      'Die Bestände der Bankkonten zum 1. Januar und zum 31. Dezember müssen auf den Kontoauszügen und in der Abrechnung dieselben sein.',
    werkzeug: 'salden',
  },
  {
    nummer: 2,
    ueblich: 'Gesamtabrechnung',
    titel: 'Gesamtabrechnung',
    kurz: 'Einnahmen, Ausgaben, Hausgelder',
    beschreibung: 'Die Gesamtabrechnung von oben nach unten durchgehen.',
    werkzeug: 'unterlagen',
  },
  {
    nummer: 3,
    ueblich: 'Kontenblätter, Kontoauszug, Belege, Jahresabrechnungen',
    titel: 'Kontenblätter und Belege',
    kurz: 'Stichproben an den Belegen',
    beschreibung: 'Die Buchungen je Konto durchsehen und zu den Buchungen die Rechnungen ansehen.',
    werkzeug: 'belege',
  },
  {
    nummer: 4,
    ueblich: 'Abrechnungsergebnis / Einzelabrechnung',
    titel: 'Abrechnungsergebnis',
    kurz: 'Guthaben, Nachzahlungen, Vermögen',
    beschreibung: 'Das Gesamtergebnis lesen.',
    werkzeug: 'unterlagen',
  },
  {
    nummer: 5,
    ueblich: 'Wirtschaftsplan (Kontrollblatt)',
    titel: 'Wirtschaftsplan',
    kurz: 'Plan des Folgejahres, Rücklage',
    beschreibung: 'Den Wirtschaftsplan mit Plan und Ist vergleichen und die Prüfung abschließen.',
    werkzeug: 'abschluss',
  },
];

let demoSchritte: Schritt[] = schritteFrisch();
let demoSalden: Saldenzeile[] = [];

function schritteFrisch(): Schritt[] {
  return SCHRITT_VORLAGEN.map((v) => ({
    ...v,
    status: 'offen',
    anmerkung: null,
    begonnenAm: null,
    erledigtVon: null,
    erledigtAm: null,
  }));
}

function demoFortschritt() {
  return demoSchritte.filter((x) => x.status === 'erledigt').length / demoSchritte.length;
}

/**
 * Zahlungsbefunde im Klickdummy.
 *
 * Bewusst dieselbe Logik wie im Server, nur gegen den Bestand im Speicher:
 * sonst zeigt der Dummy etwas anderes als die echte Anwendung, und genau
 * das ist der Fehler, den ein Klickdummy nicht machen darf.
 */
function befundeNeu(): Map<string, Zahlungsbefund> {
  const karte = new Map<string, Zahlungsbefund>();
  const keinKontoauszug = bestand.transaktionen.length === 0;

  for (const beleg of bestand.belege) {
    const meine = bestand.zuordnungen.filter(
      (z) => z.belegId === beleg.id && z.status === 'zugeordnet',
    );
    const zugeordneterBetrag = Math.round(meine.reduce((s, z) => s + z.betrag, 0) * 100) / 100;
    const daten = meine
      .map((z) => bestand.transaktionen.find((t) => t.id === z.transaktionId)?.buchungsdatum)
      .filter((d): d is string => Boolean(d))
      .sort();
    const lautKontoauszug = daten[0] ?? null;
    const lautBuchhaltung = beleg.zahldatum;
    const abweichungTage =
      lautBuchhaltung && lautKontoauszug
        ? Math.round(
            (Date.parse(lautBuchhaltung) - Date.parse(lautKontoauszug)) / 86_400_000,
          )
        : null;
    const offen = beleg.brutto === null ? null : Math.round((beleg.brutto - zugeordneterBetrag) * 100) / 100;

    const status: Zahlungsbefund['status'] = keinKontoauszug
      ? 'kein_kontoauszug'
      : !meine.length
        ? 'keine_zahlung'
        : offen !== null && offen > 0.005
          ? 'teilweise'
          : abweichungTage !== null && Math.abs(abweichungTage) > 3
            ? 'datum_weicht_ab'
            : 'bezahlt';

    karte.set(beleg.id, {
      belegId: beleg.id,
      zahldatumLautBuchhaltung: lautBuchhaltung,
      zahldatumLautKontoauszug: lautKontoauszug,
      abweichungTage,
      zugeordneterBetrag,
      offen,
      transaktionIds: meine.map((z) => z.transaktionId),
      status,
    });
  }
  return karte;
}

/**
 * Die Gegenstücke zu den REST-Endpunkten. Gleiche Namen, gleiche Rückgaben —
 * deshalb kennt der Rest der Oberfläche den Unterschied nicht.
 */

// ─────────────────────────────────────────────────────────────
// Journal
// ─────────────────────────────────────────────────────────────

/**
 * Ein Journal zum Vorführen.
 *
 * Gebaut aus den Belegen des Dummys, mit genau den Abweichungen, um die es in
 * einer Prüfung geht: zwei Buchungen ohne Beleg, drei Belege, die im Journal
 * fehlen, und eine, deren Betrag nicht stimmt. Ein Journal ohne Befunde wäre
 * zum Herzeigen wertlos — zu sehen ist ja gerade, was das Werkzeug findet.
 *
 * Die Kontonummern bekommen die führende Null des Ausdrucks, die Belege
 * tragen die iDWELL-Schreibweise mit Objektsuffix. Dass beides zusammenfindet,
 * ist eine der Stellen, an denen ein Abgleich in der Praxis scheitert.
 */
function journalErzeugen(dateiname: string): Journal {
  const zeilen: Journalzeile[] = [];
  const zuordnungen: Journalzuordnung[] = [];
  // Die letzten drei Belege bleiben im Journal ungebucht.
  const gebucht = bestand.belege.slice(0, Math.max(0, bestand.belege.length - 3));

  gebucht.forEach((b, i) => {
    const konto = (b.buchungszeilen[0]?.sachkontoNummer ?? '4210').split('_')[0];
    // Bei einem Beleg weicht der gebuchte Betrag ab — Teilzahlung oder
    // Zahlendreher, genau der Fall, den ein Beirat sehen will.
    const abweichend = i === 4;
    const betrag = runde2((b.brutto ?? 0) + (abweichend ? 48.22 : 0));
    const id = `jz${i + 1}`;
    zeilen.push({
      id,
      laufnummer: zeilen.length + 1,
      seite: 1 + Math.floor(i / 24),
      datum: b.zahldatum ?? b.rechnungsdatum ?? '2025-01-01',
      belegnummer: String(i + 1),
      text: `${b.lieferant ?? 'Ohne Lieferant'}${b.rechnungsnummer ? `, ${b.rechnungsnummer}` : ''}`,
      kontoSoll: konto.padStart(5, '0'),
      kontoHaben: '01200',
      ustProzent: null,
      betrag,
    });
    zuordnungen.push({
      id: `jzu${i + 1}`,
      zeileId: id,
      belegId: b.id,
      status: abweichend ? 'vorschlag' : 'zugeordnet',
      konfidenz: abweichend ? 0.5 : 1,
      quelle: 'automatisch',
      begruendung: abweichend ? 'Lieferant im Buchungstext' : 'Betrag gleich, Lieferant im Buchungstext',
    });
  });

  // Zwei Buchungen, zu denen es keinen Beleg gibt.
  for (const [i, extra] of [
    { text: 'Dauerauftrag Hausmeisterdienst', betrag: 1240, konto: '04210' },
    { text: 'Kontoführungsentgelt', betrag: 86.4, konto: '08070' },
  ].entries()) {
    const id = `jx${i + 1}`;
    zeilen.push({
      id,
      laufnummer: zeilen.length + 1,
      seite: 1,
      datum: '2025-05-02',
      belegnummer: String(900 + i),
      text: extra.text,
      kontoSoll: extra.konto,
      kontoHaben: '01200',
      ustProzent: null,
      betrag: extra.betrag,
    });
  }

  const summe = runde2(zeilen.reduce((s, z) => s + z.betrag, 0));
  return {
    vorhanden: true,
    dateiname,
    liegenschaftNummer: '055',
    liegenschaftBezeichnung: bestand.pruefung.liegenschaft_bez,
    periodeVon: bestand.pruefung.periode_von,
    periodeBis: bestand.pruefung.periode_bis,
    kontrolle: {
      anzahlLautDokument: zeilen.length,
      summeLautDokument: summe,
      anzahlGelesen: zeilen.length,
      summeGelesen: summe,
      stimmt: true,
    },
    zahlungskonto: '01200',
    hinweise: [],
    importiertAm: new Date().toISOString(),
    importiertVon: 'Hausverwaltung',
    zeilen,
    zuordnungen,
    befunde: [],
    summeJournal: summe,
    summeBelege: runde2(bestand.belege.reduce((s, b) => s + (b.brutto ?? 0), 0)),
  };
}

/**
 * Die Befunde des Dummys.
 *
 * Eine verkürzte Fassung dessen, was der Server rechnet — sie muss nur auf
 * Änderungen reagieren, die jemand im Dummy vornimmt, damit „kein Beleg
 * nötig" und „zuordnen" sichtbar etwas bewirken.
 */
function journalBefundeNeu(): Journal['befunde'] {
  const j = bestand.journal;
  if (!j?.zeilen) return [];
  const zuordnung = new Map((j.zuordnungen ?? []).map((z) => [z.zeileId, z]));
  const belegNach = new Map(bestand.belege.map((b) => [b.id, b]));
  const vergeben = new Set(
    (j.zuordnungen ?? []).map((z) => z.belegId).filter((id): id is string => Boolean(id)),
  );
  const befunde: NonNullable<Journal['befunde']> = [];

  for (const z of j.zeilen) {
    if (!zuordnung.has(z.id)) {
      befunde.push({
        art: 'buchung_ohne_beleg',
        zeilenIds: [z.id],
        belegId: null,
        text: `${z.text} — gebucht am ${z.datum}, kein Beleg gefunden.`,
        betrag: Math.abs(z.betrag),
      });
    }
  }
  for (const b of bestand.belege) {
    if (!vergeben.has(b.id)) {
      befunde.push({
        art: 'beleg_ohne_buchung',
        zeilenIds: [],
        belegId: b.id,
        text: `${b.lieferant ?? 'Ohne Lieferant'} — im Journal nicht gefunden. Entweder nicht verbucht oder in einem anderen Jahr.`,
        betrag: Math.abs(b.brutto ?? 0),
      });
    }
  }
  for (const [zeileId, zu] of zuordnung) {
    const zeile = j.zeilen.find((z) => z.id === zeileId);
    const beleg = zu.belegId ? belegNach.get(zu.belegId) : null;
    if (!zeile || !beleg?.brutto) continue;
    const unterschied = runde2(Math.abs(zeile.betrag) - Math.abs(beleg.brutto));
    if (Math.abs(unterschied) > 0.005) {
      befunde.push({
        art: 'betrag_weicht_ab',
        zeilenIds: [zeileId],
        belegId: beleg.id,
        text: `${beleg.lieferant ?? 'Ohne Lieferant'}: Beleg ${beleg.brutto.toFixed(2)}, gebucht ${Math.abs(zeile.betrag).toFixed(2)} — Differenz ${unterschied.toFixed(2)}.`,
        betrag: Math.abs(unterschied),
      });
    }
  }
  return befunde;
}

function journalKennzahlen() {
  const j = bestand.journal;
  if (!j?.vorhanden) {
    return {
      journalVorhanden: false,
      journalZeilen: 0,
      journalSumme: 0,
      journalKontrolleStimmt: false,
      journalBefunde: 0,
      journalVorschlaege: 0,
    };
  }
  return {
    journalVorhanden: true,
    journalZeilen: j.zeilen?.length ?? 0,
    journalSumme: j.summeJournal ?? 0,
    journalKontrolleStimmt: j.kontrolle?.stimmt ?? false,
    journalBefunde: (journalBefundeNeu() ?? []).length,
    journalVorschlaege: (j.zuordnungen ?? []).filter((z) => z.status === 'vorschlag').length,
  };
}

export const demoApi = {
  pruefung: () =>
    gleich({
      pruefung: bestand.pruefung,
      rolle,
      name: rolle === 'beirat' ? 'Heinz Berger' : 'Hausverwaltung',
      hausverwaltung: 'Muster Hausverwaltung',
    }),

  schritte: () => gleich({ schritte: demoSchritte, fortschritt: demoFortschritt() }),

  schrittSetzen: (nummer: number, status: SchrittStatus, anmerkung?: string | null) => {
    const jetzt = new Date().toISOString();
    demoSchritte = demoSchritte.map((s) =>
      s.nummer === nummer
        ? {
            ...s,
            status,
            anmerkung: anmerkung ?? s.anmerkung,
            begonnenAm: status === 'offen' ? null : (s.begonnenAm ?? jetzt),
            erledigtVon: status === 'erledigt' ? 'Heinz Berger' : null,
            erledigtAm: status === 'erledigt' ? jetzt : null,
          }
        : s,
    );
    return gleich({ schritte: demoSchritte, fortschritt: demoFortschritt() });
  },

  dokumente: () => gleich({ kategorien: [], dokumente: [] }),

  dokumentStatus: (_dokumentId: string, _status: PruefStatus | undefined, _vermerk?: string | null) => gleich({ ok: true as const, dokument: undefined as Dokument | undefined }),

  salden: () => gleich(demoSalden),

  saldenSpeichern: (zeilen: Partial<Saldenzeile>[]) => {
    const gleichOderNull = (a: number | null | undefined, b: number | null | undefined) =>
      a === null || a === undefined || b === null || b === undefined ? null : Math.abs(a - b) < 0.005;
    demoSalden = zeilen
      .filter((z) => z.konto)
      .map((z, i) => ({
        id: z.id ?? `s${i}`,
        konto: z.konto!,
        iban: z.iban ?? null,
        anfangAuszug: z.anfangAuszug ?? null,
        endeAuszug: z.endeAuszug ?? null,
        anfangAbrechnung: z.anfangAbrechnung ?? null,
        endeAbrechnung: z.endeAbrechnung ?? null,
        anfangStimmt: gleichOderNull(z.anfangAuszug, z.anfangAbrechnung),
        endeStimmt: gleichOderNull(z.endeAuszug, z.endeAbrechnung),
        geaendertVon: 'Heinz Berger',
        geaendertAm: new Date().toISOString(),
      }));
    return gleich(demoSalden);
  },

  kennzahlen: () => gleich(kennzahlenNeu()),

  belege: () => gleich({ belege: bestand.belege, gliederung: bestand.gliederung }),

  statusSetzen: (belegId: string, status: PruefStatus) => {
    const beleg = bestand.belege.find((b) => b.id === belegId);
    if (beleg) {
      beleg.pruefstatus = status;
      beleg.pruefstatusVon = rolle === 'beirat' ? 'Heinz Berger' : 'Hausverwaltung';
      beleg.pruefstatusAm = new Date().toISOString();
    }
    return gleich({ ok: true as const });
  },

  zahlungen: () => gleich(Object.fromEntries(befundeNeu())),

  transaktionen: () =>
    gleich({
      transaktionen: bestand.transaktionen,
      zuordnungen: bestand.zuordnungen,
      belegeJePosition: bestand.zuordnungen
        .filter((z) => z.status === 'zugeordnet')
        .reduce<Record<string, string[]>>((karte, z) => {
          (karte[z.transaktionId] ??= []).push(z.belegId);
          return karte;
        }, {}),
      ohneBeleg: bestand.transaktionen
        .filter(
          (t) =>
            t.richtung === 'ausgang' &&
            !bestand.zuordnungen.some(
              (z) =>
                z.transaktionId === t.id &&
                (z.status === 'zugeordnet' || z.status === 'nicht_belegpflichtig'),
            ),
        )
        .map((t) => t.id),
      status: Object.fromEntries(
        Object.entries(bestand.transaktionStatus).map(([id, status]) => [
          id,
          { status, von: rolle === 'beirat' ? 'Heinz Berger' : 'Hausverwaltung', am: new Date().toISOString() },
        ]),
      ),
    }),

  transaktionStatus: (transaktionId: string, status: PruefStatus) => {
    bestand.transaktionStatus[transaktionId] = status;
    return gleich({ ok: true as const });
  },

  /**
   * Verknüpfen im Klickdummy.
   *
   * Dieselbe Rechnung wie im Server: ohne Betrag wird der offene Rest des
   * Belegs genommen, gedeckelt auf das, was die Zahlung noch hergibt. Wenn
   * der Dummy hier anders rechnete als die Anwendung, wäre er als Vorführung
   * wertlos.
   */
  verknuepfen: (transaktionId: string, belegId: string, betrag?: number) => {
    const beleg = bestand.belege.find((b) => b.id === belegId);
    const transaktion = bestand.transaktionen.find((t) => t.id === transaktionId);
    const belegOffen = runde2(
      (beleg?.brutto ?? 0) -
        bestand.zuordnungen
          .filter((z) => z.belegId === belegId && z.status === 'zugeordnet')
          .reduce((s, z) => s + z.betrag, 0),
    );
    const zahlungRest = runde2(
      (transaktion?.betrag ?? 0) -
        bestand.zuordnungen
          .filter((z) => z.transaktionId === transaktionId && z.status === 'zugeordnet')
          .reduce((s, z) => s + z.betrag, 0),
    );
    const gewaehlt =
      betrag === undefined
        ? runde2(Math.min(Math.max(belegOffen, 0) || zahlungRest, Math.max(zahlungRest, 0) || belegOffen))
        : runde2(betrag);

    const id = `z-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    bestand.zuordnungen.push({
      id,
      transaktionId,
      belegId,
      betrag: gewaehlt,
      status: 'zugeordnet',
      konfidenz: 1,
      quelle: 'manuell',
      begruendung: `Von Hand verknüpft durch ${rolle === 'beirat' ? 'Heinz Berger' : 'Hausverwaltung'}`,
    });
    return gleich({ id, betrag: gewaehlt }, 300);
  },

  zuordnungBestaetigen: (zuordnungId: string) => {
    const z = bestand.zuordnungen.find((x) => x.id === zuordnungId);
    if (z) {
      z.status = 'zugeordnet';
      z.konfidenz = 1;
    }
    return gleich({ ok: true as const }, 250);
  },

  zuordnungLoesen: (zuordnungId: string) => {
    bestand.zuordnungen = bestand.zuordnungen.filter((z) => z.id !== zuordnungId);
    return gleich({ ok: true as const }, 250);
  },

  ohneBelegAbhaken: (transaktionId: string, begruendung: string) => {
    bestand.zuordnungen.push({
      id: `n-${Date.now()}`,
      transaktionId,
      belegId: '',
      betrag: 0,
      status: 'nicht_belegpflichtig',
      konfidenz: 1,
      quelle: 'manuell',
      begruendung: begruendung.trim() || 'Nicht belegpflichtig',
    });
    return gleich({ ok: true as const }, 250);
  },

  /**
   * Einladen im Klickdummy.
   *
   * Es wird nichts verschickt — aber der Ablauf ist derselbe, inklusive
   * eines Links, der aussieht wie ein echter. Dass nichts ankommt, steht
   * in der Meldung, damit es in einer Vorführung niemand missversteht.
   */
  einladen: (email: string, name?: string) => {
    const anzeige = (name ?? '').trim() || email.split('@')[0];
    return gleich(
      {
        name: anzeige,
        email,
        link: `${window.location.origin}/pruefung/${DEMO_PRUEFUNG_ID}?zugang=demo-${Math.random()
          .toString(36)
          .slice(2, 10)}`,
        gueltigBis: new Date(Date.now() + 60 * 86_400_000).toISOString(),
        verschickt: false,
        meldung:
          'Vorführfassung — es wurde keine Mail verschickt. Die laufende Anwendung verschickt hier über den hinterlegten SMTP-Zugang.',
      },
      900,
    );
  },

  kommentare: () => gleich(bestand.kommentare),

  kommentarAnlegen: (daten: {
    belegId?: string;
    transaktionId?: string;
    elternId?: string;
    text: string;
  }) => {
    const id = `k-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    bestand.kommentare.push({
      id,
      beleg_id: daten.belegId ?? null,
      transaktion_id: daten.transaktionId ?? null,
      eltern_id: daten.elternId ?? null,
      autor_name: rolle === 'beirat' ? 'Heinz Berger' : 'M. Haller, Hausverwaltung',
      autor_rolle: rolle,
      text: daten.text,
      status: rolle === 'verwaltung' ? 'beantwortet' : 'offen',
      erstellt_am: new Date().toISOString(),
    });

    // Eine Antwort der Verwaltung setzt den Strang auf beantwortet.
    if (daten.elternId && rolle === 'verwaltung') {
      const wurzel = bestand.kommentare.find((k) => k.id === daten.elternId);
      if (wurzel) wurzel.status = 'beantwortet';
    }
    // Eine neue Rückfrage des Beirats markiert den Beleg.
    if (daten.belegId && !daten.elternId && rolle === 'beirat') {
      const beleg = bestand.belege.find((b) => b.id === daten.belegId);
      if (beleg) beleg.pruefstatus = 'rueckfrage';
    }
    return gleich({ id });
  },

  kommentarErledigen: (kommentarId: string) => {
    const k = bestand.kommentare.find((x) => x.id === kommentarId);
    if (k) k.status = 'erledigt';
    return gleich({ ok: true as const });
  },

  abschliessen: (urteil: 'ohne_einwand' | 'mit_vorbehalt', anmerkung: string) => {
    const offen = bestand.kommentare.filter((k) => !k.eltern_id && k.status === 'offen').length;
    if (offen > 0 && urteil === 'ohne_einwand') {
      return Promise.reject(
        new Error(
          `Es sind noch ${offen} ${offen === 1 ? 'Rückfrage' : 'Rückfragen'} offen. Schließen Sie sie ab oder wählen Sie „mit Vorbehalt".`,
        ),
      );
    }
    bestand.pruefung.status = 'abgeschlossen';
    bestand.pruefung.urteil = urteil;
    void anmerkung;
    return gleich({ ok: true as const, offeneRueckfragen: offen });
  },


  // ── Journal
  journal: () =>
    gleich(
      bestand.journal
        ? { ...bestand.journal, befunde: journalBefundeNeu() }
        : ({ vorhanden: false } as Journal),
    ),

  /**
   * Im Klickdummy wird die Datei nicht gelesen — gezeigt wird, was danach
   * dasteht. Die Struktur ist dieselbe wie im Betrieb, nur die Herkunft
   * nicht.
   */
  journalHochladen: (datei: File) => {
    bestand.journal = journalErzeugen(datei.name);
    const k = journalKennzahlen();
    return gleich(
      {
        dateiname: datei.name,
        zeilen: k.journalZeilen,
        kontrolle: bestand.journal!.kontrolle!,
        hinweise: [
          'Vorführfassung: Die abgelegte Datei wurde nicht gelesen. Die laufende Anwendung liest den PDF-Ausdruck und vergleicht ihn mit den Kontrollsummen des Dokuments.',
        ],
        zugeordnet: (bestand.journal!.zuordnungen ?? []).filter((z) => z.status === 'zugeordnet')
          .length,
        vorschlaege: k.journalVorschlaege,
        befunde: k.journalBefunde,
      },
      1400,
    );
  },

  journalVerknuepfen: (zeileId: string, belegId: string) => {
    const j = bestand.journal;
    if (j) {
      j.zuordnungen = [
        ...(j.zuordnungen ?? []).filter((z) => z.zeileId !== zeileId),
        {
          id: `jm${zeileId}`,
          zeileId,
          belegId,
          status: 'zugeordnet',
          konfidenz: 1,
          quelle: 'manuell',
          begruendung: 'Von Hand verknüpft',
        },
      ];
    }
    return gleich({ ok: true } as const);
  },

  journalOhneBeleg: (zeileId: string) => {
    const j = bestand.journal;
    if (j) {
      j.zuordnungen = [
        ...(j.zuordnungen ?? []).filter((z) => z.zeileId !== zeileId),
        {
          id: `jo${zeileId}`,
          zeileId,
          belegId: null,
          status: 'ohne_beleg',
          konfidenz: 1,
          quelle: 'manuell',
          begruendung: 'Von Hand als belegfrei festgehalten',
        },
      ];
    }
    return gleich({ ok: true } as const);
  },

  journalVorschlagAnnehmen: (zeileId: string) => {
    const z = (bestand.journal?.zuordnungen ?? []).find((x) => x.zeileId === zeileId);
    if (z) z.status = 'zugeordnet';
    return gleich({ ok: true } as const);
  },

  journalLoesen: (zeileId: string) => {
    const j = bestand.journal;
    if (j) j.zuordnungen = (j.zuordnungen ?? []).filter((z) => z.zeileId !== zeileId);
    return gleich({ ok: true } as const);
  },

  /** Die Drehung gilt auch im Dummy für alle — hier eben für alle Tabs dieses Fensters. */
  belegDrehen: (belegId: string, grad: 0 | 90 | 180 | 270) => {
    const beleg = bestand.belege.find((b) => b.id === belegId);
    if (beleg) beleg.drehung = grad;
    return gleich({ ok: true as const, drehung: grad });
  },

  /** Im Klickdummy wird nicht wirklich hochgeladen — der Ablauf wird gezeigt. */
  bankHochladen: (dateien: File[]) =>
    gleich(
      {
        neu: 0,
        dubletten: 0,
        hinweise: dateien.map(
          (d) =>
            `${d.name}: In dieser Vorführfassung werden keine Dateien verarbeitet. Die laufende Anwendung liest CAMT.053, MT940 und PDF-Kontoauszüge und legt die Buchungen an.`,
        ),
      },
      1800,
    ),

  bankAbgleichen: () => {
    const k = kennzahlenNeu();
    return gleich(
      {
        kennzahlen: {
          transaktionen: k.transaktionenGesamt,
          zugeordnet: k.transaktionenZugeordnet,
          vorschlaege: new Set(
            bestand.zuordnungen.filter((z) => z.status === 'vorschlag').map((z) => z.transaktionId),
          ).size,
          sammelzahlungen: 0,
          quote: k.abgleichquote,
          nichtZugeordneterBetrag: 0,
        },
        ohneBeleg: [],
        ohneZahlung: [],
      },
      900,
    );
  },
};
